// src/pages/admin/Page_Leads/PromotionsTab.js
//
// Promociones personales - pestana "Promociones" del panel comercial.
//
// Creador de promociones personales: nombre + email + plan + % de descuento
// sobre el plan ANUAL elegido + duracion en dias. Al crear se genera un codigo
// de un solo uso ligado a ese email (cupon Stripe restringido a ESE plan) y se
// envia el correo de oferta con urgencia. El correo de enganche sale solo desde
// el cron (mitad de la ventana / 2 dias antes de caducar) o manualmente aqui.
//
// El envio pasa SIEMPRE por una confirmacion con el email en grande: el codigo
// queda ligado a lo que se teclee, asi que una errata lo quema sin aviso (el
// lead nunca recibe el correo, y si lo recibiera el candado por email lo
// rechazaria). La confirmacion ademas dice si ese email nos consta de algo.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Segmented,
  Button,
  Table,
  Tag,
  Alert,
  Row,
  Col,
  Statistic,
  Typography,
  Tooltip,
  Popconfirm,
  Modal,
  message,
} from "antd";
import {
  GiftOutlined,
  SendOutlined,
  StopOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../../../hooks/useAuth";
import { promotionsApi } from "../../../api/promotionsApi";

const { Text } = Typography;

const STATE_TAG = {
  active: { color: "green", label: "Activa" },
  redeemed: { color: "gold", label: "Canjeada" },
  expired: { color: "default", label: "Caducada" },
  cancelled: { color: "red", label: "Cancelada" },
};

// Etapa de embudo: que ha HECHO el destinatario. Espejo de computeFunnelStage
// en server/services/promo/promoCodeService.js.
const STAGE = {
  pendiente_envio: {
    color: "red",
    label: "Correo no salio",
    help: "La promo se creo pero el correo fallo. Reintenta con 'Enviar enganche'.",
  },
  sin_reaccion: {
    color: "default",
    label: "Sin reaccion",
    help: "Se le escribio y aun no ha entrado a ver su oferta. Sigue en plazo.",
  },
  interesado: {
    color: "blue",
    label: "Entro a mirar",
    help: "Pulso el enlace del correo y vio su oferta, pero todavia no ha comprado.",
  },
  convertido: {
    color: "green",
    label: "Compro",
    help: "Canjeo el codigo y completo el pago.",
  },
  perdido_tibio: {
    color: "orange",
    label: "Miro y caduco",
    help: "Entro a ver la oferta pero se le paso el plazo. El mejor candidato para reintentar.",
  },
  perdido_frio: {
    color: "default",
    label: "Caduco sin entrar",
    help: "Nunca llego a abrir su oferta y el codigo ya vencio.",
  },
  cancelada: { color: "red", label: "Cancelada", help: "Anulada a mano." },
};

const stageMeta = (s) =>
  STAGE[s] || { color: "default", label: s || "-", help: "" };

// Filtros rapidos de la tabla. Las dos primeras son las que de verdad se
// trabajan: a quien perseguir y quien merece un segundo intento.
const FILTROS = [
  { key: "todas", label: "Todas", test: () => true },
  {
    key: "interesado",
    label: "Entraron a mirar",
    test: (r) => r.stage === "interesado",
  },
  {
    key: "perdido_tibio",
    label: "Miraron y caducaron",
    test: (r) => r.stage === "perdido_tibio",
  },
  {
    key: "sin_reaccion",
    label: "Sin reaccion",
    test: (r) => r.stage === "sin_reaccion",
  },
  {
    key: "convertido",
    label: "Compraron",
    test: (r) => r.stage === "convertido",
  },
  {
    key: "problemas",
    label: "Correo no salio",
    test: (r) => r.stage === "pendiente_envio",
  },
];

// Planes que admiten promocion. Espejo de server/services/promo/promoPlans.js.
const PLAN_OPTIONS = [
  { value: "enterprise", label: "Educa-Centro", tag: "blue" },
  { value: "creator", label: "Educa-Creator", tag: "purple" },
  { value: "standard", label: "Educa-Estandar", tag: "cyan" },
];

const planLabel = (key) =>
  PLAN_OPTIONS.find((p) => p.value === key)?.label || key || "-";

const planColor = (key) =>
  PLAN_OPTIONS.find((p) => p.value === key)?.tag || "default";

export function PromotionsTab() {
  const { accessToken } = useAuth();
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filtro, setFiltro] = useState("todas");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [actioningId, setActioningId] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const res = await promotionsApi.list(accessToken);
      setItems(res?.data?.items || []);
      setSummary(res?.data?.summary || null);
    } catch (err) {
      message.error(`Error cargando promociones: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  // Envio real, ya confirmado por el usuario.
  const doCreate = useCallback(
    async (values) => {
      setCreating(true);
      try {
        const res = await promotionsApi.create(accessToken, {
          name: values.name,
          email: values.email,
          discount_pct: values.discount_pct,
          duration_days: values.duration_days,
          plan_key: values.plan_key,
        });
        const promo = res?.data?.promotion;
        setLastCreated({ promo, emailSent: !!res?.data?.emailSent });
        if (res?.data?.emailSent) {
          message.success(`Promocion creada y correo enviado a ${promo.email}`);
        } else {
          message.warning(
            "Promocion creada pero el correo fallo. Usa 'Enviar enganche' para reintentar.",
          );
        }
        form.resetFields();
        load();
      } catch (err) {
        message.error(err.message || "Error creando la promocion");
      } finally {
        setCreating(false);
      }
    },
    [accessToken, form, load],
  );

  // Paso previo al envio: confirmar el email en grande. El codigo queda ligado
  // a lo que se teclee aqui y el correo sale en el mismo clic, asi que esta es
  // la ultima oportunidad de detectar una errata.
  const handleCreate = useCallback(
    async (values) => {
      const email = String(values.email || "").trim();

      // Comprobacion de cortesia. Si falla (o el email es nuevo) seguimos
      // igualmente: es informacion para decidir, nunca un bloqueo.
      let check = null;
      try {
        const res = await promotionsApi.checkEmail(accessToken, email);
        check = res?.data || null;
      } catch {
        check = null;
      }

      Modal.confirm({
        title: "Confirma antes de enviar",
        icon: <ExclamationCircleOutlined />,
        width: 560,
        okText: "Enviar la promocion",
        cancelText: "Revisar",
        content: (
          <div>
            <p style={{ marginBottom: 8 }}>
              Vas a crear un codigo del <strong>{values.discount_pct}%</strong>{" "}
              sobre <strong>{planLabel(values.plan_key)} anual</strong> y enviar
              el correo a:
            </p>
            <div
              style={{
                fontFamily: "Consolas, monospace",
                fontSize: 18,
                fontWeight: 700,
                wordBreak: "break-all",
                background: "#f6f8fa",
                border: "1px solid #d9e1e8",
                borderRadius: 8,
                padding: "10px 12px",
                color: "#0E2A5C",
              }}
            >
              {email}
            </div>
            {check && !check.known && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message="Este email no nos consta"
                description="No aparece en leads, usuarios, clientes de Stripe ni promociones anteriores. Puede ser un contacto nuevo (normal) o una errata. Comprueba que esta bien escrito: el codigo queda ligado a este email y no se puede cambiar."
              />
            )}
            {check?.known && (
              <Alert
                type="success"
                showIcon
                style={{ marginTop: 12 }}
                message={`Email conocido (${check.sources.join(", ")})`}
              />
            )}
          </div>
        ),
        onOk: () => doCreate({ ...values, email }),
      });
    },
    [accessToken, doCreate],
  );

  const handleReminder = useCallback(
    async (promo) => {
      setActioningId(promo._id);
      try {
        await promotionsApi.sendReminder(accessToken, promo._id);
        message.success(`Correo de enganche enviado a ${promo.email}`);
        load();
      } catch (err) {
        message.error(err.message || "Error enviando el enganche");
      } finally {
        setActioningId(null);
      }
    },
    [accessToken, load],
  );

  const handleCancel = useCallback(
    async (promo) => {
      setActioningId(promo._id);
      try {
        await promotionsApi.cancel(accessToken, promo._id);
        message.success(`Promocion ${promo.code} cancelada`);
        load();
      } catch (err) {
        message.error(err.message || "Error cancelando");
      } finally {
        setActioningId(null);
      }
    },
    [accessToken, load],
  );

  const itemsFiltrados = useMemo(() => {
    const f = FILTROS.find((x) => x.key === filtro) || FILTROS[0];
    return items.filter(f.test);
  }, [items, filtro]);

  const columns = useMemo(
    () => [
      {
        title: "Fecha",
        dataIndex: "created_at",
        width: 100,
        render: (v) => (
          <Tooltip title={dayjs(v).format("YYYY-MM-DD HH:mm")}>
            <span>{dayjs(v).format("DD/MM/YYYY")}</span>
          </Tooltip>
        ),
      },
      {
        title: "Persona",
        key: "person",
        render: (_, row) => {
          // created_by trae "campana:<nombre>" cuando la promo la genero una
          // campana. Distinguirlo importa: al entrar 376 de golpe, las que se
          // crearon a mano quedarian sepultadas sin esta marca.
          const deCampana =
            typeof row.created_by === "string" &&
            row.created_by.startsWith("campana:");
          return (
            <div>
              <div style={{ fontWeight: 600 }}>{row.name}</div>
              <div style={{ color: "#64748b", fontSize: 12 }}>{row.email}</div>
              {deCampana && (
                <Tooltip title={row.created_by.slice("campana:".length)}>
                  <Tag color="geekblue" style={{ fontSize: 11, marginTop: 2 }}>
                    Campana
                  </Tag>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Codigo",
        dataIndex: "code",
        width: 170,
        render: (v) => (
          <Text copyable style={{ fontFamily: "Consolas, monospace" }}>
            {v}
          </Text>
        ),
      },
      {
        title: "Plan",
        dataIndex: "plan_key",
        width: 130,
        render: (v) => <Tag color={planColor(v)}>{planLabel(v)}</Tag>,
      },
      {
        title: "Dto.",
        dataIndex: "discount_pct",
        width: 70,
        align: "right",
        render: (v) => <Tag color="gold">-{v}%</Tag>,
      },
      {
        title: "Caduca",
        dataIndex: "expires_at",
        width: 130,
        render: (v, row) => (
          <div>
            <div>{dayjs(v).format("DD/MM/YYYY")}</div>
            {row.state === "active" && (
              <div style={{ color: "#64748b", fontSize: 12 }}>
                {row.days_left} dia{row.days_left === 1 ? "" : "s"} restantes
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Situacion",
        dataIndex: "stage",
        width: 150,
        render: (v, row) => {
          const m = stageMeta(v);
          return (
            <Tooltip title={m.help}>
              <div>
                <Tag color={m.color}>{m.label}</Tag>
                {row.state === "active" && (
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>
                    {STATE_TAG[row.state].label}
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        // El indicador que pediste: entro o no entro, y cuantas veces volvio.
        // Volver varias veces es senal de interes real: merece una llamada.
        title: "Entro",
        key: "entrada",
        width: 130,
        render: (_, row) =>
          row.validated_at ? (
            <Tooltip
              title={`Primera vez: ${dayjs(row.validated_at).format("DD/MM/YYYY HH:mm")}${
                row.validated_count > 1
                  ? ` · Ultima: ${dayjs(row.last_validated_at).format("DD/MM HH:mm")}`
                  : ""
              }`}
            >
              <div>
                <Tag color="blue" icon={<EyeOutlined />}>
                  {dayjs(row.validated_at).format("DD/MM")}
                </Tag>
                {row.validated_count > 1 && (
                  <div style={{ fontSize: 11, color: "#2563eb" }}>
                    {row.validated_count} visitas
                  </div>
                )}
              </div>
            </Tooltip>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Text>
          ),
      },
      {
        title: "Correos",
        key: "emails",
        width: 170,
        render: (_, row) => (
          <div style={{ fontSize: 12, color: "#475569" }}>
            <div>
              Oferta:{" "}
              {row.email_sent_at ? (
                dayjs(row.email_sent_at).format("DD/MM HH:mm")
              ) : (
                <Text type="danger" style={{ fontSize: 12 }}>
                  no salio
                </Text>
              )}
            </div>
            <div>
              Enganche:{" "}
              {row.reminder_sent_at ? (
                dayjs(row.reminder_sent_at).format("DD/MM HH:mm")
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  pendiente
                </Text>
              )}
            </div>
          </div>
        ),
      },
      {
        title: "Acciones",
        key: "actions",
        width: 210,
        render: (_, row) => (
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              size="small"
              icon={<SendOutlined />}
              disabled={row.state !== "active"}
              loading={actioningId === row._id}
              onClick={() => handleReminder(row)}
            >
              Enviar enganche
            </Button>
            <Popconfirm
              title="Cancelar esta promocion"
              description="El codigo dejara de funcionar inmediatamente."
              okText="Cancelar promo"
              cancelText="Volver"
              onConfirm={() => handleCancel(row)}
              disabled={row.state === "redeemed" || row.state === "cancelled"}
            >
              <Button
                size="small"
                danger
                icon={<StopOutlined />}
                disabled={row.state === "redeemed" || row.state === "cancelled"}
              />
            </Popconfirm>
          </div>
        ),
      },
    ],
    [actioningId, handleReminder, handleCancel],
  );

  return (
    <div style={{ maxWidth: 1100 }}>
      <Alert
        type="info"
        showIcon
        icon={<GiftOutlined />}
        style={{ marginBottom: 16 }}
        message="Promociones personales"
        description="Cada promocion genera un codigo de UN solo uso ligado al email indicado, con descuento sobre el primer año del plan ANUAL que elijas. El correo (oferta y enganche) se adapta al plan. Al crearla se envia la oferta con urgencia; si el lead no aplica el codigo, el enganche sale automaticamente a mitad de la promocion (o 2 dias antes de caducar), y tambien puedes enviarlo manualmente."
      />

      <Card title="Nueva promocion" size="small" style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout="inline"
          onFinish={handleCreate}
          initialValues={{
            discount_pct: 30,
            duration_days: 7,
            plan_key: "enterprise",
          }}
        >
          <Form.Item
            name="name"
            rules={[
              { required: true, message: "Nombre obligatorio" },
              { min: 2, max: 80, message: "Entre 2 y 80 caracteres" },
            ]}
          >
            <Input placeholder="Nombre de la persona" style={{ width: 190 }} />
          </Form.Item>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: "Email obligatorio" },
              { type: "email", message: "Email no valido" },
            ]}
          >
            <Input
              placeholder="Email del destinatario"
              style={{ width: 230 }}
            />
          </Form.Item>
          <Form.Item
            name="plan_key"
            label="Plan"
            rules={[{ required: true, message: "Plan obligatorio" }]}
          >
            <Select options={PLAN_OPTIONS} style={{ width: 170 }} />
          </Form.Item>
          <Form.Item
            name="discount_pct"
            label="Descuento"
            rules={[{ required: true, message: "%" }]}
          >
            <InputNumber
              min={5}
              max={90}
              addonAfter="%"
              style={{ width: 110 }}
            />
          </Form.Item>
          <Form.Item
            name="duration_days"
            label="Duracion"
            rules={[{ required: true, message: "Dias" }]}
          >
            <InputNumber
              min={1}
              max={60}
              addonAfter="dias"
              style={{ width: 120 }}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<GiftOutlined />}
              loading={creating}
            >
              Crear y enviar correo
            </Button>
          </Form.Item>
        </Form>
        <Text type="secondary" style={{ display: "block", marginTop: 10 }}>
          El descuento aplica sobre la modalidad ANUAL del plan elegido. El
          codigo caduca a los dias indicados y solo funciona con el email del
          destinatario: revisalo bien antes de enviar, porque no se puede
          cambiar despues.
        </Text>
        {lastCreated?.promo && (
          <Alert
            type={lastCreated.emailSent ? "success" : "warning"}
            showIcon
            style={{ marginTop: 12 }}
            message={
              <span>
                Codigo{" "}
                <Text
                  copyable
                  strong
                  style={{ fontFamily: "Consolas, monospace" }}
                >
                  {lastCreated.promo.code}
                </Text>{" "}
                creado para {lastCreated.promo.email} (
                {planLabel(lastCreated.promo.plan_key)}, -
                {lastCreated.promo.discount_pct}%, caduca{" "}
                {dayjs(lastCreated.promo.expires_at).format("DD/MM/YYYY")}).{" "}
                {lastCreated.emailSent
                  ? "Correo de oferta enviado."
                  : "El correo fallo: reintenta con 'Enviar enganche'."}
              </span>
            }
          />
        )}
      </Card>

      {summary && summary.total > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} sm={8} md={4}>
              <Statistic title="Promociones" value={summary.total} />
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Statistic
                title="Correo enviado"
                value={summary.enviadas}
                valueStyle={
                  summary.enviadas < summary.total
                    ? { color: "#c63f3e" }
                    : undefined
                }
              />
            </Col>
            <Col xs={12} sm={8} md={5}>
              <Statistic
                title="Entraron a mirar"
                value={summary.entraron}
                suffix={
                  <span style={{ fontSize: 14, color: "#64748b" }}>
                    ({summary.tasa_entrada}%)
                  </span>
                }
                valueStyle={{ color: "#2563eb" }}
              />
            </Col>
            <Col xs={12} sm={8} md={5}>
              <Statistic
                title="Compraron"
                value={summary.convertidas}
                suffix={
                  <span style={{ fontSize: 14, color: "#64748b" }}>
                    ({summary.tasa_conversion}% de los que entraron)
                  </span>
                }
                valueStyle={{ color: "#16a34a" }}
              />
            </Col>
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="Enganches enviados"
                value={summary.enganches}
                valueStyle={{ color: "#b45309" }}
              />
            </Col>
          </Row>
          <Text type="secondary" style={{ fontSize: 12 }}>
            "Entraron a mirar" cuenta a quien pulso el enlace de su correo y vio
            la oferta. No medimos aperturas del correo: Gmail y Apple precargan
            las imagenes y marcarian como leidos correos que nadie ha abierto.
          </Text>
        </Card>
      )}

      <Card
        title="Promociones creadas"
        size="small"
        extra={
          <Button size="small" icon={<ReloadOutlined />} onClick={load}>
            Refrescar
          </Button>
        }
      >
        <Segmented
          options={FILTROS.map((f) => ({
            label: `${f.label} (${items.filter(f.test).length})`,
            value: f.key,
          }))}
          value={filtro}
          onChange={setFiltro}
          style={{ marginBottom: 12 }}
        />
        <Table
          rowKey="_id"
          size="small"
          loading={loading}
          dataSource={itemsFiltrados}
          columns={columns}
          pagination={{
            pageSize: 25,
            hideOnSinglePage: true,
            showSizeChanger: true,
            showTotal: (t) => `${t} promociones`,
          }}
        />
      </Card>
    </div>
  );
}
